<template>
  <div class="n-input" :class="{ 'has-error': dateError }">
    <label :for="inputId" v-if="label" :class="{ 'sr-only': hideLabel }">{{ label }}</label>
    <input
      class="control"
      type="date"
      :id="inputId"
      :name="name"
      :value="dateValueStr"
      :disabled="disabled"
      :min="formatDate(minDate)"
      :max="formatDate(maxDate)"
      @input="(e) => handleInput((e.target as HTMLInputElement).value)"
    />
    <span v-if="dateError" class="error-message">{{ dateError }}</span>
  </div>
</template>
<script lang="ts" setup>
import { useIdGenerator } from '@/shared/designsystem/useIdGenerator';
import { Ref, ref, watch } from 'vue';
import { useVModel } from '@vueuse/core';

defineOptions({
  name: 'n-date-picker',
});

const props = defineProps({
  id: {
    type: String,
  },
  label: {
    type: String,
  },
  name: {
    type: String,
    default: '',
  },
  modelValue: {
    type: Date,
    required: true,
  },
  min: {
    type: Date,
  },
  max: {
    type: Date,
  },
  errorMessage: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hideLabel: {
    type: String,
  },
});

const { inputId } = useIdGenerator(props.id);

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date): void;
}>();
const value = useVModel(props, 'modelValue', emit);

const minDate = ref<Date>();
const maxDate = ref<Date>();
const dateValueStr = ref('');
const dateError = ref('');

watch(
  () => props.errorMessage,
  () => (dateError.value = props.errorMessage || ''),
  { immediate: true },
);

watch(
  value,
  () => {
    if (!value.value) {
      return;
    }
    if (!(value.value instanceof Date)) {
      // This is needed to make the storybook work because the storybook control passes a unix timestamp
      dateValueStr.value = formatDate(new Date(value.value));
    } else if (!Number.isNaN(value.value.getTime())) {
      dateValueStr.value = formatDate(value.value);
    }
  },
  { immediate: true },
);

watch(
  () => props.min,
  () => {
    if (!(props.min instanceof Date) && props.min !== undefined) {
      minDate.value = new Date(props.min);
    } else {
      minDate.value = props.min;
    }
  },
  { immediate: true },
);

watch(
  () => props.max,
  () => {
    if (!(props.max instanceof Date) && props.max !== undefined) {
      maxDate.value = new Date(props.max);
    } else {
      maxDate.value = props.max;
    }
  },
  { immediate: true },
);

function handleInput(strValue: string) {
  const dateVal = new Date(strValue);
  value.value = dateVal;
}

function formatDate(date: Date | undefined) {
  if (date && date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toJSON().slice(0, 10);
  }
  return '';
}
</script>

<style lang="scss" scoped>
@use '../../../styles/accessibility' as *;
@use '../../../styles/colours' as *;
@use '../../../styles/input' as *;

.n-input {
  input[type='date'] {
    cursor: text;

    &::-webkit-datetime-edit {
      cursor: text;
    }
    &::-webkit-calendar-picker-indicator {
      cursor: pointer;
      &:focus,
      &:active {
        outline: none;
      }
    }
  }
}
</style>
